"use client";

import { useMemo } from "react";
import BrandSelector from "@/components/listing/BrandSelector";
import ListingFamilySpecFieldsHost from "@/components/listing/ListingFamilySpecFieldsHost";
import ModelInput from "@/components/listing/ModelInput";
import SizeInput from "@/components/listing/SizeInput";
import { activeSubcategoryIdSet } from "@/lib/activeCategories";
import { getCategoryFieldConfig } from "@/lib/listing/fieldConfigs";
import { sellListingShowsModelField } from "@/lib/listing/listingIdentityPolicy";
import { getFamilyRuntime } from "@/lib/listing/listingFamilyRuntime";
import type { ListingFamilySpecs } from "@/lib/listing/listingFamilyRuntime";
import {
  listingSpecPluginHidesBrandLine,
  listingSpecPluginHidesModelLine,
  listingSpecPluginHidesSizeLine,
} from "@/lib/listing/listingSpecPlugin";
import { getStepTwoSizePresentation } from "@/lib/listing/listingSpecSellPresentation";
import type { CategoryStructure } from "@/lib/categoryStructure";
import type { ItemCategory } from "@/lib/types";

export type RequestItemPreferenceFieldsProps = {
  categoryId: ItemCategory;
  subcategoryId: string;
  /** Active catalog structure (same as sell/CategoryDropdown) for size presentation */
  categoryStructure: CategoryStructure[];
  brand: string;
  size: string;
  model: string;
  familySpecs: ListingFamilySpecs;
  onBrandChange: (brand: string) => void;
  onSizeChange: (size: string) => void;
  onModelChange: (model: string) => void;
  onFamilySpecsChange: (next: ListingFamilySpecs) => void;
};

/**
 * Optional demand-capture prefs — same controls and visibility as sell Step 2
 * (BrandSelector, SizeInput, ModelInput, ListingFamilySpecFieldsHost), all optional.
 */
export default function RequestItemPreferenceFields({
  categoryId,
  subcategoryId,
  categoryStructure,
  brand,
  size,
  model,
  familySpecs,
  onBrandChange,
  onSizeChange,
  onModelChange,
  onFamilySpecsChange,
}: RequestItemPreferenceFieldsProps) {
  const config = getCategoryFieldConfig(categoryId);
  const hasFamily = Boolean(getFamilyRuntime(subcategoryId));

  const activeSubcategoryIds = useMemo(
    () => activeSubcategoryIdSet(categoryStructure),
    [categoryStructure],
  );

  const sizePresentation = useMemo(() => {
    if (!config?.size.enabled) return null;
    return getStepTwoSizePresentation({
      category: categoryId,
      subcategoryId,
      activeSubcategoryIds,
      fallback: config.size,
    });
  }, [config, categoryId, subcategoryId, activeSubcategoryIds]);

  const showBrand =
    Boolean(config?.brand) && !listingSpecPluginHidesBrandLine(subcategoryId);
  const showSize =
    Boolean(config?.size.enabled) &&
    !listingSpecPluginHidesSizeLine(subcategoryId);
  const showModel =
    sellListingShowsModelField(Boolean(config?.model)) &&
    !listingSpecPluginHidesModelLine(subcategoryId);

  if (!hasFamily && !showBrand && !showSize && !showModel) {
    return null;
  }

  return (
    <div className="space-y-4 rounded-lg border border-border p-4">
      <div>
        <h3 className="text-sm font-medium text-foreground">
          Optional details
        </h3>
        <p className="text-xs text-text-light mt-0.5">
          Same options as listing — skip anything you don’t care about. We’ll
          still show related items.
        </p>
      </div>

      {hasFamily ? (
        <ListingFamilySpecFieldsHost
          subcategoryId={subcategoryId}
          familySpecs={familySpecs}
          onChange={onFamilySpecsChange}
          optionalMode
          className=""
        />
      ) : null}

      {(showBrand || showSize || showModel) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {showBrand ? (
            <div>
              <label className="block mb-2 text-sm font-medium text-foreground">
                Brand{" "}
                <span className="text-text-light font-normal text-xs">
                  (optional)
                </span>
              </label>
              <BrandSelector
                value={brand}
                onChange={onBrandChange}
                category={categoryId}
                placeholder="Search and choose a brand"
                className="w-full"
              />
              <p className="mt-1 text-xs text-text-light">
                Optional — skip if you don&apos;t see your brand
              </p>
            </div>
          ) : null}

          {showSize && config ? (
            <div>
              <label className="block mb-2 text-sm font-medium text-foreground">
                {sizePresentation?.fieldLabel ?? config.size.label ?? "Size"}{" "}
                <span className="text-text-light font-normal text-xs">
                  (optional)
                </span>
              </label>
              <SizeInput
                value={size}
                onChange={onSizeChange}
                config={sizePresentation?.sizeInputConfig ?? config.size}
                helperTextOverride={
                  sizePresentation?.helperTextOverride ?? null
                }
                className="w-full"
              />
            </div>
          ) : null}

          {showModel ? (
            <div
              className={showBrand || showSize ? "sm:col-span-2" : undefined}
            >
              <label className="block mb-2 text-sm font-medium text-foreground">
                Model{" "}
                <span className="text-text-light font-normal text-xs">
                  (optional)
                </span>
              </label>
              <ModelInput
                value={model}
                onChange={onModelChange}
                brand={brand || null}
                subcategory={subcategoryId}
                placeholder="Enter model name (optional)"
                className="w-full"
              />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** Whether this subcategory has any optional preference UI (sell Step 2 parity). */
export function requestItemHasPreferenceFields(
  categoryId: ItemCategory | string | null | undefined,
  subcategoryId: string | null | undefined,
): boolean {
  if (!categoryId || !subcategoryId) return false;
  const config = getCategoryFieldConfig(categoryId);
  if (getFamilyRuntime(subcategoryId)) return true;
  if (config?.brand && !listingSpecPluginHidesBrandLine(subcategoryId)) {
    return true;
  }
  if (config?.size.enabled && !listingSpecPluginHidesSizeLine(subcategoryId)) {
    return true;
  }
  if (
    sellListingShowsModelField(Boolean(config?.model)) &&
    !listingSpecPluginHidesModelLine(subcategoryId)
  ) {
    return true;
  }
  return false;
}
